import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, EnvelopeSimple, Globe, Buildings } from '@phosphor-icons/react';

export function Terms() {
  return (
    <div className="min-h-screen bg-background" data-testid="terms-page">
      {/* Header */}
      <div className="glass-effect border-b border-border sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={18} className="mr-1" /> Back
              </Button>
            </Link>
            <h1 className="text-xl font-heading">TERMS AND CONDITIONS</h1>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="prose prose-invert max-w-none">
          {/* Header Info */}
          <div className="mb-8 p-6 bg-card border border-border rounded-lg">
            <h2 className="text-2xl font-heading mt-0 mb-2">Stay On Hoops</h2>
            <p className="text-sm text-primary font-medium mb-4">A Product of Intuitive Mentors Organization</p>
            <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
              <a href="https://www.stayonhoops.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                <Globe size={16} /> www.stayonhoops.com
              </a>
              <a href="mailto:info@stayonhoops.com" className="flex items-center gap-2 hover:text-primary transition-colors">
                <EnvelopeSimple size={16} /> info@stayonhoops.com
              </a>
            </div>
            <p className="mt-4 text-sm text-muted-foreground mb-0">
              <strong>Effective Date:</strong> February 1, 2026
            </p>
          </div>
          
          {/* Operating Entity Section */}
          <section className="mb-8 p-6 bg-primary/5 border border-primary/20 rounded-lg">
            <h3 className="text-lg font-heading text-primary flex items-center gap-2 mt-0">
              <Buildings size={20} weight="duotone" />
              OPERATING ENTITY
            </h3>
            <p>
              Stay On Hoops is a basketball program and digital platform operated by <strong>Intuitive Mentors Organization</strong>, a youth-focused organization dedicated to mentorship, development, and education through sport and community programming.
            </p>
            <p className="mb-0">
              All references to &quot;Stay On Hoops,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot; refer to Intuitive Mentors Organization.
            </p>
          </section>
          
          {/* Section 1 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">1. PROGRAM OVERVIEW</h3>
            <p>
              Stay On Hoops is a program and product operated by Intuitive Mentors Organization (&quot;Intuitive Mentors,&quot; &quot;we,&quot; &quot;our,&quot; or &quot;us&quot;). It is an organized youth basketball program for participants ages 9–14. The program emphasizes structured pickup-style basketball in a non-refereed environment.
            </p>
            <p className="font-medium">
              Stay On Hoops is not a school, daycare, league, or training service.
            </p>
          </section>
          
          {/* Section 2 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">2. ELIGIBILITY</h3>
            <ul>
              <li>Participants must be between 9 and 14 years old</li>
              <li>A parent or legal guardian must create the account and complete registration</li>
              <li>A signed waiver is required before participation</li>
              <li>We reserve the right to deny or revoke participation for safety, conduct, or rule violations</li>
            </ul>
          </section>
          
          {/* Section 3 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">3. PARENTAL CONSENT (COPPA COMPLIANCE)</h3>
            <p>
              Intuitive Mentors Organization, through Stay On Hoops, complies with the Children&apos;s Online Privacy Protection Act (COPPA).
            </p>
            <p>By creating an account and registering a child, the parent or legal guardian:</p>
            <ul>
              <li>Confirms they are the child&apos;s legal guardian</li>
              <li>Provides verifiable parental consent for the collection and use of the child&apos;s information</li>
              <li>Understands that child data is used only for program operations</li>
            </ul>
            <p>
              Parents may review, update, or request deletion of their child&apos;s information at any time by contacting{' '}
              <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline">info@stayonhoops.com</a>.
            </p>
          </section>
          
          {/* Section 4 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">4. REGISTRATION & ACCOUNTS</h3>
            <ul>
              <li>Parents are responsible for providing accurate information</li>
              <li>One account may manage multiple children</li>
              <li>Accounts may not be shared</li>
              <li>We may suspend or terminate accounts that violate these Terms</li>
            </ul>
          </section>
          
          {/* Section 5 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">5. WAIVERS & ASSUMPTION OF RISK</h3>
            <p className="font-medium">
              Basketball is a physical activity that involves inherent risks.
            </p>
            <p>A valid waiver must be signed by a parent or legal guardian before a child may:</p>
            <ul>
              <li>Register for sessions</li>
              <li>Check in</li>
              <li>Participate in any Stay On Hoops activity</li>
            </ul>
            <p>
              Failure to maintain a valid waiver may result in denied participation without refund.
            </p>
          </section>
          
          {/* Section 6 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">6. SESSIONS, CAPACITY & CHECK-IN</h3>
            <ul>
              <li>Sessions are capacity-limited (typically 20–25 players)</li>
              <li>Registration is first-come, first-served</li>
              <li>Waitlists may apply</li>
              <li>Check-in does not guarantee playing time</li>
              <li>Stay On Hoops reserves the right to remove participants for unsafe or disrespectful behavior</li>
            </ul>
          </section>
          
          {/* Section 7 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">7. PAYMENTS & REFUNDS</h3>
            <p>If fees apply:</p>
            <ul>
              <li>Payments are processed securely via Stripe</li>
              <li>Registration is not confirmed until payment is completed</li>
              <li>Refunds are discretionary and may not be available for missed sessions</li>
              <li>Removal for conduct does not qualify for a refund</li>
            </ul>
          </section>
          
          {/* Section 8 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">8. CONDUCT & SUPERVISION</h3>
            <p>Participants and parents must:</p>
            <ul>
              <li>Respect players, staff, and facilities</li>
              <li>Refrain from sideline coaching</li>
              <li>Follow safety expectations</li>
            </ul>
            <p>
              Stay On Hoops staff supervise for safety only and do not provide instruction during gameplay.
            </p>
          </section>
          
          {/* Section 9 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">9. INTELLECTUAL PROPERTY</h3>
            <p>
              All content on www.stayonhoops.com is owned by Intuitive Mentors Organization and may not be used without permission.
            </p>
          </section>
          
          {/* Section 10 - Updated */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">10. LIMITATION OF LIABILITY</h3>
            <p>
              To the fullest extent permitted by California law, Intuitive Mentors Organization, operating as Stay On Hoops, is not liable for injuries, losses, or damages arising from participation except where prohibited by law.
            </p>
          </section>
          
          {/* Section 11 - Updated */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">11. CALIFORNIA GOVERNING LAW</h3>
            <p>
              These Terms are governed by the laws of the State of California, and apply to all services provided by Intuitive Mentors Organization through Stay On Hoops.
            </p>
          </section>
          
          {/* Section 12 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">12. CHANGES TO TERMS</h3>
            <p>
              We may update these Terms periodically. Continued use of the website or services constitutes acceptance of updated Terms.
            </p>
          </section>
          
          {/* Section 13 */}
          <section className="mb-8">
            <h3 className="text-lg font-heading text-primary">13. CONTACT</h3>
            <p>
              <a href="mailto:info@stayonhoops.com" className="text-primary hover:underline flex items-center gap-2">
                <EnvelopeSimple size={18} /> info@stayonhoops.com
              </a>
            </p>
            <p className="text-sm text-muted-foreground">
              Operated by Intuitive Mentors Organization
            </p>
          </section>
        </div>
        
        {/* Footer Links */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/privacy" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Privacy Policy
          </Link>
          <span className="hidden sm:inline text-muted-foreground">•</span>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary transition-colors">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
